import React, { useState, useEffect } from 'react';
import { 
  DocumentArrowDownIcon, 
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  PresentationChartLineIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface AnalyticsSummary {
  topPerformingStructures: StructurePerformance[];
  structureTypePerformance: TypePerformance[];
  clientRetention: {
    returningClients: number;
    newClients: number;
    retentionRate: number;
  };
  revenueGrowth: {
    currentPeriod: number;
    previousPeriod: number;
    growthRate: number;
  };
  occupancyRate: {
    overall: number;
    led: number;
    cityboard: number;
  };
  seasonalTrends: SeasonalTrend[];
}

interface StructurePerformance {
  id: number;
  code: string;
  location: string;
  revenue: number;
  occupancyRate: number;
  averageOrderValue: number;
}

interface TypePerformance {
  type: string;
  revenue: number;
  occupancyRate: number;
  count: number;
  percentage: number;
}

interface SeasonalTrend {
  period: string;
  value: number;
}

const AnalyticsReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [period, setPeriod] = useState('year');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Effect to fetch data when period or custom dates change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let startToFetch = startDate;
      let endToFetch = endDate;
      let updateStateDates = false;

      if (period !== 'custom') {
        const today = new Date();
        const calculatedStart = new Date();
        if (period === 'month') {
          calculatedStart.setMonth(today.getMonth() - 1);
        } else if (period === 'quarter') {
          calculatedStart.setMonth(today.getMonth() - 3);
        } else if (period === 'year') {
          calculatedStart.setFullYear(today.getFullYear() - 1);
        }
        const calculatedStartDateStr = calculatedStart.toISOString().split('T')[0];
        const calculatedEndDateStr = today.toISOString().split('T')[0];

        // Use calculated dates if period is not custom
        startToFetch = calculatedStartDateStr;
        endToFetch = calculatedEndDateStr;
        // Flag to update state only if it differs from calculated
        if (startDate !== calculatedStartDateStr || endDate !== calculatedEndDateStr) {
           updateStateDates = true;
        }
      }

      // Ensure we have valid dates before fetching (especially for initial 'custom' state)
      if (!startToFetch || !endToFetch) {
        // If dates are invalid (e.g., initial load with period='custom' but no dates set),
        // set default to 'year' to fetch initial data.
        if (period === 'custom') {
           console.log("Custom period selected but dates not set, defaulting to 'year'");
           setPeriod('year'); // This will trigger the effect again with a valid period
           setLoading(false);
           return;
        }
        // Otherwise, if dates are somehow invalid for a non-custom period, log error.
        console.error("Invalid date range for fetch:", startToFetch, endToFetch);
        setLoading(false);
        setAnalytics(null); // Clear analytics if dates are invalid
        return;
      }

      try {
        const response = await axios.get('/reports/analytics', {
          params: {
            startDate: startToFetch,
            endDate: endToFetch
          }
        });
        setAnalytics(response.data);
        // Update state dates only if they were calculated and differ
        if (updateStateDates) {
          setStartDate(startToFetch);
          setEndDate(endToFetch);
        }
      } catch (error: any) { // Added type annotation for error
        console.error('Error fetching analytics report data:', error);
        setAnalytics(null); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]); // Depend on period and the state dates
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Just update the state; the useEffect will trigger the fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
      // The useEffect hook will now handle fetching with these startDate and endDate values
    }
  };

  // Removed separate fetchAnalyticsData function as logic is integrated into useEffect
  
  const handleExportPDF = async () => {
    try {
      const response = await axios.get('/reports/analytics/export', {
        params: {
          startDate: startDate, // Explicitly pass state variable
          endDate: endDate,     // Explicitly pass state variable
          format: 'pdf'
        },
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_report_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!analytics) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-lg text-gray-600">Нет данных для аналитики</p>
          <p className="text-gray-500">Попробуйте изменить период или проверьте наличие данных в системе</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader
        title="Аналитический отчет"
        subtitle={`Данные за период: ${new Date(startDate).toLocaleDateString('ru-KZ')} - ${new Date(endDate).toLocaleDateString('ru-KZ')}`}
        actions={
          <button
            onClick={handleExportPDF}
            className="btn-secondary flex items-center"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
            Экспорт PDF
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Рост доходов</p>
              <p className={`text-2xl font-bold ${analytics.revenueGrowth.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analytics.revenueGrowth.growthRate >= 0 ? '+' : ''}
                {formatPercentage(analytics.revenueGrowth.growthRate)}
              </p>
              <p className="text-xs text-gray-500">
                По сравнению с предыдущим периодом
              </p>
            </div>
            <div className={`${analytics.revenueGrowth.growthRate >= 0 ? 'bg-green-100' : 'bg-red-100'} p-3 rounded-full`}>
              <ArrowTrendingUpIcon className={`h-6 w-6 ${analytics.revenueGrowth.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Общая занятость</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatPercentage(analytics.occupancyRate.overall)}
              </p>
              <p className="text-xs text-gray-500">
                LED: {formatPercentage(analytics.occupancyRate.led)}, 
                Ситиборды: {formatPercentage(analytics.occupancyRate.cityboard)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <PresentationChartLineIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Удержание клиентов</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatPercentage(analytics.clientRetention.retentionRate)}
              </p>
              <p className="text-xs text-gray-500">
                Новых: {analytics.clientRetention.newClients}, 
                Постоянных: {analytics.clientRetention.returningClients}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Top Performing Structures */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="text-lg font-medium mb-4">Лучшие конструкции</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Код
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Локация
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Выручка
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Занятость
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Средний заказ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.topPerformingStructures.map((structure) => (
                <tr key={structure.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {structure.code}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className=" max-w-xs">{structure.location}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatCurrency(structure.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full ${
                            structure.occupancyRate > 75 ? 'bg-green-600' : 
                            structure.occupancyRate > 50 ? 'bg-yellow-400' : 
                            'bg-red-400'
                          }`}
                          style={{ width: `${structure.occupancyRate}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-sm text-gray-600">{formatPercentage(structure.occupancyRate)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatCurrency(structure.averageOrderValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Structure Type Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Эффективность по типам конструкций</h3>
          
          <div className="space-y-4">
            {analytics.structureTypePerformance.map((type) => (
              <div key={type.type}>
                <div className="flex justify-between mb-1">
                  <div>
                    <span className="text-sm font-medium text-gray-700">{type.type}</span>
                    <span className="text-xs text-gray-500 ml-2">({type.count} шт.)</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{formatCurrency(type.revenue)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full bg-blue-600"
                    style={{ width: `${type.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{formatPercentage(type.percentage)} от общей выручки</span>
                  <span>Занятость: {formatPercentage(type.occupancyRate)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Seasonal Trends */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Сезонные тренды</h3>
          
          <div className="h-60 relative">
            {analytics.seasonalTrends.length > 0 ? (
              <>
                <div className="flex h-40 items-end space-x-2 absolute bottom-0 left-0 right-0">
                  {analytics.seasonalTrends.map((trend, index) => {
                    const maxValue = Math.max(...analytics.seasonalTrends.map(t => t.value));
                    const height = (trend.value / maxValue) * 100;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-indigo-500 hover:bg-indigo-600 transition-colors rounded-t"
                          style={{ height: `${height}%` }}
                        ></div>
                        <div className="text-xs mt-1 w-full text-center ">
                          {trend.period}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500">
                  {[100, 75, 50, 25, 0].map((value) => (
                    <div key={value} className="flex items-center">
                      <span className="mr-1">{value}%</span>
                      <div className="w-full border-t border-gray-200 border-dashed" style={{ height: 0 }}></div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Нет данных о сезонных трендах</p>
              </div>
            )}
          </div>
          
          <div className="mt-4 text-xs text-gray-500 text-center">
            График показывает относительные значения по периодам (%)
          </div>
        </div>
      </div>
      
      {/* Insights */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="text-lg font-medium mb-4">Рекомендации и анализ</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded p-4">
            <h4 className="font-medium text-gray-900 mb-2">Рост доходов</h4>
            {analytics.revenueGrowth.growthRate >= 10 ? (
              <p className="text-sm text-gray-600">
                Отличный рост доходов! Продолжайте текущую стратегию и рассмотрите увеличение числа конструкций или пересмотр ценовой политики для максимизации прибыли.
              </p>
            ) : analytics.revenueGrowth.growthRate >= 0 ? (
              <p className="text-sm text-gray-600">
                Доходы растут, но есть потенциал для улучшения. Рассмотрите новые маркетинговые стратегии и проанализируйте наиболее прибыльные конструкции для оптимизации портфеля.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Снижение доходов требует внимания. Рекомендуется проанализировать причины и разработать план по улучшению показателей: пересмотр цен, улучшение маркетинга, фокус на прибыльных клиентах.
              </p>
            )}
          </div>
          
          <div className="border rounded p-4">
            <h4 className="font-medium text-gray-900 mb-2">Занятость конструкций</h4>
            {analytics.occupancyRate.overall >= 70 ? (
              <p className="text-sm text-gray-600">
                Высокий уровень занятости указывает на высокий спрос. Рассмотрите возможность расширения сети конструкций или повышения цен.
              </p>
            ) : analytics.occupancyRate.overall >= 50 ? (
              <p className="text-sm text-gray-600">
                Средний уровень занятости. Исследуйте периоды низкой занятости и разработайте специальные предложения для их заполнения.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Низкая занятость требует анализа причин. Рекомендуем пересмотреть цены или улучшить маркетинговые усилия для привлечения клиентов.
              </p>
            )}
          </div>
          
          <div className="border rounded p-4">
            <h4 className="font-medium text-gray-900 mb-2">Клиентская база</h4>
            {analytics.clientRetention.retentionRate >= 70 ? (
              <p className="text-sm text-gray-600">
                Высокий уровень удержания клиентов показывает их лояльность. Разработайте программу лояльности для поощрения постоянных клиентов.
              </p>
            ) : analytics.clientRetention.retentionRate >= 40 ? (
              <p className="text-sm text-gray-600">
                Средний уровень удержания. Проанализируйте причины ухода клиентов и улучшите коммуникацию с существующими клиентами.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Низкий уровень удержания клиентов. Необходимо срочно пересмотреть клиентский сервис и выявить причины недовольства.
              </p>
            )}
          </div>
          
          <div className="border rounded p-4">
            <h4 className="font-medium text-gray-900 mb-2">Сезонность</h4>
            <p className="text-sm text-gray-600">
              Учитывайте сезонные колебания при планировании. Разработайте специальные предложения для периодов низкого спроса и максимизируйте доходы в пиковые периоды через оптимальное ценообразование.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; // Added missing closing brace

export default AnalyticsReport;
